import { ChangeDetectionStrategy, Component, HostBinding, Inject, Optional, ViewEncapsulation } from "@angular/core";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { MultiSessionLimit, MultiSessionLimitResponse } from "@obg/ngx.common";

import { MultiSessionLimitPeriodTranslationsEnum } from "../multi-session-limit.container.model";

import { MultiSessionLimitConfirmationModel } from "./multi-session-limit-confirmation.model";

@Component({
	selector: "obg-m-multi-session-limit-confirmation",
	templateUrl: "./multi-session-limit-confirmation.component.html",
	styleUrls: ["./multi-session-limit-confirmation.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class MultiSessionLimitConfirmationComponent {
	@HostBinding("class.obg-m-multi-session-limit-confirmation") hostClass = true;

	valueTextKeyDaily = "hours-per-day";
	valueTextKeyWeekly = "hours-per-week";
	valueTextKeyMonthly = "hours-per-month";

	constructor(
		@Optional() @Inject(MAT_DIALOG_DATA) public data: MultiSessionLimitConfirmationModel,
		private dialogRef: MatDialogRef<MultiSessionLimitConfirmationComponent>
	) {}

	onConfirm() {
		this.dialogRef.close(true);
	}

	onCancel() {
		this.dialogRef.close(false);
	}

	getPeriodText(period: string): string {
		switch (period) {
			case MultiSessionLimitPeriodTranslationsEnum.Daily:
				return this.valueTextKeyDaily;
			case MultiSessionLimitPeriodTranslationsEnum.Weekly:
				return this.valueTextKeyWeekly;
			case MultiSessionLimitPeriodTranslationsEnum.Monthly:
				return this.valueTextKeyMonthly;
			default:
				return "";
		}
	}

	isUpcomingRestricting(upcoming: MultiSessionLimit, current: MultiSessionLimitResponse): boolean {
		const upcomingPeriod = this.getSessionLimitPeriod(upcoming);
		const currentPeriod = this.getSessionLimitPeriod(current);

		if (upcomingPeriod === undefined || currentPeriod === undefined) {
			return false;
		}

		const upcomingLimit = this.getSessionLimitValue(upcoming, upcomingPeriod);
		const currentLimit = this.getSessionLimitValue(current, currentPeriod);

		if (upcomingLimit === undefined || currentLimit === undefined) {
			return false;
		}

		if (upcomingPeriod === currentPeriod) {
			return upcomingLimit <= currentLimit;
		}

		return this.calculateTightening(upcomingPeriod, currentPeriod, upcomingLimit, currentLimit);
	}

	getSessionLimitValue(sessionLimit: MultiSessionLimit, period: string): number | undefined {
		return sessionLimit[period as keyof MultiSessionLimit];
	}

	getSessionLimitPeriod(sessionLimit: MultiSessionLimit): string | undefined {
		const keys = Object.keys(sessionLimit);
		return keys.find((key) => key !== "activeFrom" && sessionLimit[key as keyof MultiSessionLimit] !== 0);
	}

	calculateTightening(
		upcomingPeriod: string,
		currentPeriod: string,
		upcomingLimit: number,
		currentLimit: number
	): boolean {
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Daily &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Weekly
		) {
			return upcomingLimit * 7 <= currentLimit;
		}
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Weekly &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Daily
		) {
			return currentLimit * 7 >= upcomingLimit;
		}
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Monthly &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Daily
		) {
			return currentLimit * 30 >= upcomingLimit;
		}
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Daily &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Monthly
		) {
			return upcomingLimit * 30 <= currentLimit;
		}
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Weekly &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Monthly
		) {
			return (upcomingLimit * 30) / 7 <= currentLimit;
		}
		if (
			upcomingPeriod === MultiSessionLimitPeriodTranslationsEnum.Monthly &&
			currentPeriod === MultiSessionLimitPeriodTranslationsEnum.Weekly
		) {
			return (currentLimit * 30) / 7 >= upcomingLimit;
		}
		return false;
	}
}
